import { BREAKPOINTS, COLORS, ComponentL, ComponentTextStyle, HyperLink, LoadingIndicator, Paginator, SystemIcons } from '@laerdal/life-react-components';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { KnowledgeArticleListItemDto } from '../model/dto/knowledgeArticleListItemDto';
import posthog from 'posthog-js';
import React, { useState, useCallback, useRef, memo } from 'react';
import ArticleApi from '../services/api/ArticleApi';

interface RelatedArticlesProps {
    articles: KnowledgeArticleListItemDto[];
    loading: boolean;
    page: number;
    pageCount: number;
    totalCount: number;
    searchTerm: string;
    onPageChange: (page: number) => void;
}

const ArticlesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ArticlesHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const DesktopPaginatorWrapper = styled.div`
  display: none;

  ${BREAKPOINTS.MEDIUM} {
    display: block;
  }

  & > nav {
    width: unset;
  }
`;

const MobilePaginatorWrapper = styled.div`
  display: flex;
  justify-content: center;

  ${BREAKPOINTS.MEDIUM} {
    display: none;
  }

  & > nav {
    width: unset;
  }
`;
const ArticlesHeader = styled.h4`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${COLORS.neutral_800};
`;

const LoadingIndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const ArticleItem = styled.div`
    border-radius: 8px;
    border: 1px solid ${COLORS.neutral_200};

  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;

  ${BREAKPOINTS.MEDIUM} {
    padding: 20px 32px;
  }
  
`;

const ArticleTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding-bottom: 8px;
  gap: 8px;

  svg {
    flex-shrink: 0;
    min-width: 20px;
    min-height: 20px;
  }
`;

const ArticleTitle = styled.span`
  color: ${COLORS.neutral_600};
  font-family: Lato;
  font-weight: 700;
  font-size: 14px;
  line-height: 150%;

  ${BREAKPOINTS.MEDIUM} {
    font-size: 18px;
  }
`;

const ArticleDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${COLORS.neutral_600};
  line-height: 1.5;
`;

const ArticleHtmlContainer = styled.div`
  color: ${COLORS.neutral_600};
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  img {
    max-width: 100%;
    height: auto !important;
  }
  table {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    max-width: 100%;
  }
  td, th {
    white-space: nowrap;
    padding: 4px 8px;
  }
  h1 {
    font-family: Lato;
    font-size: 20px !important;
    font-weight: 700;
    line-height: 120%;
    ${BREAKPOINTS.LARGE}{ font-size: 24px !important; }
  }
  h2 {
    font-family: Lato;
    font-size: 18px !important;
    font-weight: 700;
    line-height: 120%;
    ${BREAKPOINTS.LARGE}{ font-size: 22px !important; }
  }
  h3, h4, h5, h6 {
    font-family: Lato;
    font-size: 16px !important;
    font-weight: 700;
    line-height: 120%;
    ${BREAKPOINTS.LARGE}{ font-size: 20px !important; }
  }
  p, span, label, li, td, th, dd, dt, blockquote, figcaption, a {
    font-family: Lato;
    font-size: 14px !important;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    
  }
    
  hr {
    border: none;
    border-top: 1px solid #d2d2d7;
    margin: 1em 0;
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${COLORS.neutral_500};
  margin-top: 4px;
`;

const PaginatorWrapper = styled.div`
  & > nav {
    width: unset;
  }
`;

const PaginatorComponent = ({ pageCount, page, onPageChange }: { pageCount: number; page: number; onPageChange: (p: number) => void }) => (
    <Paginator
        pageCount={pageCount}
        currentPage={page}
        onPageChange={(p) => {
            onPageChange(p);
            return true;
        }}
    />
);

interface ArticleListItemProps {
    item: KnowledgeArticleListItemDto;
    isExpanded: boolean;
    isLoading: boolean;
    content: string | undefined;
    onToggle: (articleNumber: string) => void;
    t: (key: string) => string;
    language: string;
}

const ArticleListItem = memo(({ item, isExpanded, isLoading, content, onToggle, t, language }: ArticleListItemProps) => (
    <ArticleItem>
        <ArticleTitleRow onClick={() => onToggle(item.articleNumber)}>
            <ArticleTitle>
                {item.title}
            </ArticleTitle>
            <span style={{ display: 'flex', width: 20, height: 20 }}>
                {isExpanded
                    ? <SystemIcons.ChevronUp size='20px' />
                    : <SystemIcons.ChevronDown size='20px' />
                }
            </span>
        </ArticleTitleRow>
        {isExpanded ? (
            isLoading ? (
                <LoadingIndicatorContainer>
                    <LoadingIndicator />
                </LoadingIndicatorContainer>
            ) : content ? (
                <ArticleHtmlContainer
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            ) : (
                <ArticleDescription>{item.description}</ArticleDescription>
            )
        ) : (
            <ArticleDescription>
                {item.description.length > 250
                    ? `${item.description.substring(0, 250)}...`
                    : item.description}
            </ArticleDescription>
        )}
        <ArticleMeta>
            <span>{item.readTime} {t("min read")}</span>
            {item.updatedAt && (
                <>
                    <SystemIcons.Bullet size='8px' />
                    <span>{new Date(item.updatedAt).toLocaleDateString(language, { month: "short", day: "numeric", year: "numeric" })}</span>
                </>
            )}
            <SystemIcons.Bullet size='8px' />
            <span>{item.viewCount} {t("views")}</span>
        </ArticleMeta>
    </ArticleItem>
));

const RelatedArticles = ({ articles, loading, page, pageCount, totalCount, searchTerm, onPageChange }: RelatedArticlesProps) => {
    const { t, i18n } = useTranslation("cases");
    const navigate = useNavigate();
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [articleContent, setArticleContent] = useState<Record<string, string>>({});
    const [loadingContent, setLoadingContent] = useState<Set<string>>(new Set());

    const articleContentRef = useRef(articleContent);
    articleContentRef.current = articleContent;

    const toggleExpand = useCallback((articleNumber: string) => {
        setExpandedItems(prev => {
            if (prev.has(articleNumber)) {
                const next = new Set(prev);
                next.delete(articleNumber);
                return next;
            }
            const next = new Set(prev);
            next.add(articleNumber);

            posthog.capture('SUPP-Casedeflection-ArticlesView', { articleNumber });
            if (!articleContentRef.current[articleNumber]) {
                setLoadingContent(p => { const n = new Set(p); n.add(articleNumber); return n; });
                ArticleApi.GetKnowledgeArticle(articleNumber, undefined)
                    .then(data => {
                        if (data?.publicContent) {
                            setArticleContent(p => ({ ...p, [articleNumber]: data.publicContent! }));
                        }
                    })
                    .finally(() => {
                        setLoadingContent(p => { const n = new Set(p); n.delete(articleNumber); return n; });
                    });
            }
            return next;
        });
    }, []);

    return (
        <ArticlesSection>
            <ArticlesHeaderRow>
                <ArticlesHeader>{t("Related articles")} ({totalCount})</ArticlesHeader>
                {pageCount > 1 && (
                    <DesktopPaginatorWrapper>
                        <PaginatorComponent pageCount={pageCount} page={page} onPageChange={onPageChange} />
                    </DesktopPaginatorWrapper>
                )}
            </ArticlesHeaderRow>
            {loading && (
                <LoadingIndicatorContainer>
                    <LoadingIndicator />
                </LoadingIndicatorContainer>
            )}
            {!loading && articles.length > 0 && (
                <>
                    {articles.map((item) => (
                        <ArticleListItem
                            key={item.articleNumber}
                            item={item}
                            isExpanded={expandedItems.has(item.articleNumber)}
                            isLoading={loadingContent.has(item.articleNumber)}
                            content={articleContent[item.articleNumber]}
                            onToggle={toggleExpand}
                            t={t}
                            language={i18n.language}
                        />
                    ))}
                </>
            )}
            {!loading && articles.length === 0 && (
                <ComponentL textStyle={ComponentTextStyle.Regular}>
                    {t("No related articles found.")}
                </ComponentL>
            )}
            {pageCount > 1 && (
                <MobilePaginatorWrapper>
                    <PaginatorComponent pageCount={pageCount} page={page} onPageChange={onPageChange} />
                </MobilePaginatorWrapper>
            )}
        </ArticlesSection>
    );
};

export default RelatedArticles;
